import { z } from 'zod'

const publicEventPriceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unknown') }),
  z.object({ kind: z.literal('free') }),
  z.object({ kind: z.literal('fixed'), amount: z.string() }),
  z.object({ kind: z.literal('range'), min: z.string(), max: z.string() }),
])

/** A single published event as rendered on the SEO-first public event finder. */
export const publicEventListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** The short link name: the event page lives at `/events/{slug}` when it has one. */
  slug: z.string().nullable().optional(),
  description: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  startTime: z.string(),
  endTime: z.string(),
  price: publicEventPriceSchema,
  city: z.string(),
  country: z.string(),
  lat: z.number(),
  lng: z.number(),
  /** The venue is announced later; lat/lng are the city centre. */
  locationPending: z.boolean().optional(),
  /** Only guests see the address; lat/lng are rounded to some kilometres. */
  addressHidden: z.boolean().optional(),
  categoryName: z.string().nullable(),
  interestedCount: z.number().int(),
  /** Whether the event page belongs in search indexes and sitemaps. */
  indexable: z.boolean().optional(),
  updatedAt: z.string(),
})

/** A single published business listing as rendered on the public finder. */
export const publicLocationListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  nicknames: z.array(z.string()),
  description: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  city: z.string(),
  country: z.string(),
  lat: z.number(),
  lng: z.number(),
  rating: z.number(),
  reviewCount: z.number(),
  categoryName: z.string().nullable(),
  interestedCount: z.number().int(),
  updatedAt: z.string(),
})

export const publicEventsResponseSchema = z.object({
  items: z.array(publicEventListItemSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  hasMore: z.boolean(),
})

export const publicLocationsResponseSchema = z.object({
  items: z.array(publicLocationListItemSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  hasMore: z.boolean(),
})

/** Lightweight enumeration consumed by the public sitemap generator. */
export const publicSitemapSchema = z.object({
  events: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      slug: z.string().nullable().optional(),
      updatedAt: z.string(),
    }),
  ),
  locations: z.array(z.object({ id: z.string(), name: z.string(), updatedAt: z.string() })),
  cities: z.array(
    z.object({
      city: z.string(),
      country: z.string(),
      eventCount: z.number().int(),
      locationCount: z.number().int(),
    }),
  ),
})

export type PublicEventListItem = z.infer<typeof publicEventListItemSchema>
export type PublicLocationListItem = z.infer<typeof publicLocationListItemSchema>
export type PublicEventsResponse = z.infer<typeof publicEventsResponseSchema>
export type PublicLocationsResponse = z.infer<typeof publicLocationsResponseSchema>
export type PublicSitemap = z.infer<typeof publicSitemapSchema>
